import React, { useContext, useEffect, useRef, useState } from 'react'
import anime from 'animejs'
import delay from 'delay'
import clsx from 'clsx'
import { Button, Paper, Snackbar, useTheme } from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning';
import ShieldIcon from '@mui/icons-material/Shield'
import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import hipersendService from '../../services/hipersend'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import Text from '../Text'
import ConfirmationModal from '../ConfirmationModal'
import metaWhite from '../../assets/meta-white-color.png'
import metaPrimary from '../../assets/meta-primary-color.png'
import { APP_VERSION } from '../../services/appVersion'

const API_VERSIONS_OFICIAL = ['hiperapi-v3-cloud-official']

function DeviceDisabled({ onDisconnect }) {
  const { getCredentials, server, fetchData: getApiData, provider } = useContext(ApiDataWrapperContext)
  const theme = useTheme()
  const phoneRef = useRef()
  const serverRef = useRef()
  const pipeRef = useRef()

  const [restartApiButtonEnabled, setRestartApiButtonEnabled] = useState(true)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [isOpenConfirmationModalDesconnectInstance, setIsOpenConfirmationModalDesconnectInstance] = useState(false)
  const [isOpenConfirmationModalRestartApi, setIsOpenConfirmationModalRestartApi] = useState(false)

  const handleAnimation = async () => {
    anime({
      targets: phoneRef.current,
      translateX: [250, 0],
      opacity: [0, 1],
      easing: 'easeOutExpo'
    })
    anime({
      targets: serverRef.current,
      translateX: [-250, 0],
      opacity: [0, 1],
      easing: 'easeOutExpo'
    })
    await delay(500)
    anime({
      targets: pipeRef.current,
      translateY: [10, 0],
      opacity: [0, 1],
      easing: 'easeOutExpo'
    })
  }

  const handleExpireQrCode = async () => {
    try {
      const { api, secret } = getCredentials()
      await hipersendService.notificacoesInteligentes.expireQrCode(api, secret)
      onDisconnect()
    } catch (error) {
      console.error(error)
    }
  }

  const handleRestartApi = async () => {
    try {
      const { api, secret } = getCredentials()
      setRestartApiButtonEnabled(false)
      await hipersendService.notificacoesInteligentes.activateApi(api, secret)
      setSnackbarMessage('Api reiniciada com sucesso')
      setTimeout(() => {
        setRestartApiButtonEnabled(true);
      }, 300000);
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    handleAnimation()
    getApiData()
  }, [])

  return (
    <div>
      <ConfirmationModal
        open={isOpenConfirmationModalRestartApi}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Reativar api
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra PROSSEGUIR para reativar a api"
        challengeAwnswer="PROSSEGUIR"
        onClose={() => {
          setIsOpenConfirmationModalRestartApi(false)
        }}
        onConfirm={handleRestartApi}
      >
        <Text>Deseja realmente reativar a api?</Text>
      </ConfirmationModal>
      <Snackbar
        open={!!snackbarMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
      {/* illustration */}
      <div className="flex items-center justify-between">
        {/* cellphone */}
        <div ref={phoneRef} className="opacity-0">
          <div
            className={clsx('relative w-[120px] md:w-[240px] h-[190px] md:h-[370px] rounded-md md:rounded-lg', {
              'bg-[#202124ff]': theme.isDark,
              'bg-[#ccccccff]': !theme.isDark,
            })}
          >
            <div
              className={clsx('absolute left-1/2 -translate-x-1/2 top-[5%] w-[30%] h-[10px] rounded-md bg-white', {
                'opacity-[0.1]': theme.isDark,
                'opacity-[0.7]': !theme.isDark
              })}
            />
            <div className="absolute top-[12%] -translate-x-1/2 left-1/2 rounded-md md:rounded-lg h-[70%] w-[75%]">
              <div
                className={clsx('relative w-full h-full bg-white  rounded-md md:rounded-lg', {
                  'opacity-[0.1]': theme.isDark,
                  'opacity-[0.7]': !theme.isDark
                })}
              />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50px] md:w-[100px] h-[50px] md:h-[100px] bg-[#f0ad4e] rounded-full flex justify-center items-center">
                <div className="hidden md:block">
                  <WarningIcon className="text-white" style={{ fontSize: 60 }} />
                </div>
                <div className="block md:hidden">
                  <WarningIcon className="text-white" style={{ fontSize: 30 }} />
                </div>
              </div>
            </div>
            <div
              className={clsx('absolute left-1/2 -translate-x-1/2 bottom-[5%] w-[20px] md:w-[30px] h-[20px] md:h-[30px] rounded-full bg-white', {
                'opacity-[0.1]': theme.isDark,
                'opacity-[0.7]': !theme.isDark
              })}
            />
          </div>
        </div>
        {/* connection */}
        <div ref={pipeRef} className="relative w-full opacity-0">
          {
            API_VERSIONS_OFICIAL.includes(provider) &&
            <div className="absolute top-[20px] md:top-[-80px] w-full">
              <div className="flex items-center justify-center space-x-2">
                <div className="relative">
                  <img src={theme.isDark ? metaWhite : metaPrimary} className="w-[150px]" />
                  <div className="flex justify-end mt-[3px]">
                    <span
                      style={{ backgroundColor: theme.palette.primary.main }}
                      className="font-black px-1 text-white text-xs tracking-wider"
                    >v{APP_VERSION}</span>
                  </div>
                </div>
              </div>
            </div>
          }
          <div
            className={clsx('relative w-full h-[20px] overflow-hidden', {
              'bg-[#202124ff]': theme.isDark,
              'bg-[#20212442]': !theme.isDark
            })}
          >
            {/* <div className="absolute w-[40%] h-full animate-left-to-right animate-infinite animate-duration-[0.6s] bg-[#f0ad4e]" /> */}
          </div>
          <div className="absolute top-[120px] md:top-[60px] w-full">
            <div className="flex items-center justify-center space-x-2">
              <ShieldIcon style={{ color: '#f0ad4e', fontSize: 35 }} />
              <p className="text-[#f0ad4e] font-black text-xs md:text-3xl">DESATIVADA</p>
            </div>
          </div>
        </div>
        {/* server */}
        <div ref={serverRef} className="opacity-0">
          <div
            className={clsx('relative w-[120px] md:w-[240px] h-[190px] md:h-[370px] rounded-md md:rounded-lg', {
              'bg-[#202124ff]': theme.isDark,
              'bg-[#ccccccff]': !theme.isDark
            })}
          >
            <div className="p-4 md:p-8 flex flex-col h-full">
              <div className="grid grid-cols-12 gap-[3px] md:gap-2">
                <div
                  className={clsx('col-span-10 h-[20px] md:h-[50px] rounded-md', {
                    'bg-white opacity-[0.05]': theme.isDark,
                    'bg-gray-800 opacity-[0.2]': !theme.isDark
                  })}
                >

                </div>
                <div className="col-span-2 grid grid-cols-1 gap-[3px] md:gap-2 h-[20px] md:h-[50px]">
                  <div className="bg-[#f0ad4e] rounded-md"></div>
                  <div className="bg-[#f0ad4e] rounded-md"></div>
                </div>
              </div>
              <div className="my-auto">
                <Text
                  className={clsx('text-xs md:text-2xl font-black text-center', {
                    'text-white opacity-[0.5]': theme.isDark,
                    'text-black opacity-[0.5]': !theme.isDark
                  })}
                >SERVIDOR HS</Text>
                <Text
                  className={clsx('md:text-4xl font-black text-center', {
                    'text-white': theme.isDark,
                    'text-black opacity-[0.5]': !theme.isDark
                  })}
                >{server}</Text>
              </div>
              <div className="flex justify-end space-x-1 md:space-x-2">
                <div className="w-[10px] md:w-[20px] h-[10px] md:h-[20px] bg-emerald-600 rounded-full" />
                <div className="w-[10px] md:w-[20px] h-[10px] md:h-[20px] bg-emerald-600 rounded-full" />
                <div className="w-[10px] md:w-[20px] h-[10px] md:h-[20px] bg-emerald-600 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <Text
          className={clsx(
            'md:text-1xl mt-5 font-bold text-center leading-tight',
            {
              'text-white': theme.isDark,
              'text-black opacity-70': !theme.isDark
            }
          )}
        >
          Sua API foi desativada por falta de uso nos últimos 45 dias. Para ativá-la, clique no botão abaixo para reativar a API.
        </Text>
      </div>
      <div className="mt-20 flex justify-center space-x-8">
        <Button onClick={() => setIsOpenConfirmationModalRestartApi(true)} disabled={!restartApiButtonEnabled}>
          REATIVAR API
        </Button>
      </div>
    </div>
  )
}

export default DeviceDisabled